import React, { useEffect, useState } from "react";
import {
  Modal,
  BlockStack,
  InlineGrid,
  Text,
  Divider,
  Spinner,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import { formatPrice, formatDate } from "../../utility/funcs";

/**
 * Detail modal that expands a payment (transaction) or a single invoice into the
 * set of invoices + reservations it covers. Opened from /cash (keyed by
 * transactionId) and /invoices (keyed by invoiceId). One payment can span several
 * invoices, and one invoice several reservations — see get_payment_reservations.
 * Kept intentionally minimal: reservation id + amount, grouped under each invoice.
 */
function PaymentDetailModal({ open, onClose, transactionId = null, invoiceId = null }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || (!transactionId && !invoiceId)) return;
    let cancelled = false;
    setLoading(true);
    setData(null);

    (async () => {
      const { data: res, error } = await supabase.rpc("get_payment_reservations", {
        p_transaction_id: transactionId,
        p_invoice_id: invoiceId,
      });
      if (cancelled) return;
      if (error) {
        console.error("get_payment_reservations:", error.message);
        setData({ payment: null, invoices: [] });
      } else {
        setData(res || { payment: null, invoices: [] });
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, transactionId, invoiceId]);

  const payment = data?.payment || null;
  const invoices = data?.invoices || [];

  // Card line ("Tarjeta de crédito •••• 1234") when the payment was on a card.
  const cardLine = payment?.card_type
    ? `${
        payment.card_type === "credit"
          ? "Tarjeta de crédito"
          : "Tarjeta de débito"
      }${payment.card_last4 ? ` •••• ${payment.card_last4}` : ""}`
    : null;

  const goToReservation = (id) => {
    onClose();
    navigate(`/reservation?id=${id}`);
  };

  return (
    <Modal open={open} onClose={onClose} title="Detalle del pago">
      <Modal.Section>
        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spinner size="small" />
          </div>
        ) : invoices.length === 0 ? (
          <BlockStack gap="300">
            {payment && (
              <>
                <InlineGrid columns="1fr auto">
                  <Text variant="headingSm">
                    {payment.entity_name || "Pago"}
                  </Text>
                  <Text variant="headingSm">{formatPrice(payment.amount)}</Text>
                </InlineGrid>
                {(payment.account_name || payment.date) && (
                  <Text tone="subdued" variant="bodySm">
                    {payment.account_name || ""}
                    {payment.account_name && payment.date ? " · " : ""}
                    {payment.date ? formatDate(payment.date) : ""}
                  </Text>
                )}
                {cardLine && (
                  <Text tone="subdued" variant="bodySm">
                    {cardLine}
                  </Text>
                )}
                <Divider />
              </>
            )}
            <Text tone="subdued">
              {payment
                ? "Este pago no está aplicado a ninguna factura."
                : "Este documento no tiene facturas ni reservaciones asociadas."}
            </Text>
          </BlockStack>
        ) : (
          <BlockStack gap="400">
            {payment && (
              <BlockStack gap="050">
                <InlineGrid columns="1fr auto">
                  <Text variant="headingSm">{payment.entity_name || "Pago"}</Text>
                  <Text variant="headingSm">{formatPrice(payment.amount)}</Text>
                </InlineGrid>
                {cardLine && (
                  <Text tone="subdued" variant="bodySm">
                    {cardLine}
                  </Text>
                )}
              </BlockStack>
            )}

            {invoices.map((inv) => (
              <BlockStack key={inv.id} gap="150">
                <Divider />
                <Text variant="bodyMd" fontWeight="semibold">
                  Factura {inv.invoice_number || `#${inv.alegra_id || inv.id}`}
                </Text>

                {(inv.reservations || []).length === 0 ? (
                  <Text variant="bodySm" tone="subdued">
                    Sin reservaciones vinculadas.
                  </Text>
                ) : (
                  inv.reservations.map((r) => (
                    <InlineGrid key={r.id} columns="1fr auto">
                      <span
                        role="button"
                        onClick={() => goToReservation(r.id)}
                        style={{
                          color: "#005bd3",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        Reserva #{r.id}
                      </span>
                      <Text variant="bodySm">
                        {formatPrice(r.total_price || 0)}
                      </Text>
                    </InlineGrid>
                  ))
                )}
              </BlockStack>
            ))}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

export default PaymentDetailModal;
